import { useCallback, type ReactNode } from 'react';
import { MAX_AGE, MIN_AGE } from '@anon-chat/shared';
import { haptic } from '@/shared/telegram';

const percent = (value: number): number => ((value - MIN_AGE) / (MAX_AGE - MIN_AGE)) * 100;

const TRACK_CLASS =
  'pointer-events-none absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-border';

/**
 * Общие стили ползунка. Нативный `input[type=range]` выбран намеренно: он даёт
 * доступность и корректную работу с тач-жестами в WebView Telegram, чего
 * самописный drag не обеспечивает без заметных усилий.
 */
const THUMB_CLASS =
  'h-11 w-full cursor-pointer appearance-none bg-transparent ' +
  '[&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:w-6 ' +
  '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full ' +
  '[&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white ' +
  '[&::-webkit-slider-thumb]:bg-accent [&::-webkit-slider-thumb]:shadow-md ' +
  '[&::-moz-range-thumb]:h-6 [&::-moz-range-thumb]:w-6 [&::-moz-range-thumb]:rounded-full ' +
  '[&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:bg-accent';

export function AgeSlider({
  value,
  onChange,
  label,
  compact = false,
}: {
  value: number;
  onChange: (value: number) => void;
  label: string;
  /** Плотная вёрстка: анкета целиком помещается на один экран. */
  compact?: boolean;
}): ReactNode {
  const handleChange = useCallback(
    (next: number) => {
      if (next !== value) {
        haptic.select();
      }
      onChange(next);
    },
    [onChange, value],
  );

  return (
    <div className={compact ? 'rounded-2xl border border-border bg-surface px-4 py-3' : 'rounded-2xl border border-border bg-surface p-5'}>
      <div className={compact ? 'mb-1 flex items-baseline justify-between' : 'mb-4 text-center'}>
        <span className={compact ? 'text-[13px] text-text-muted' : 'order-2 mt-1 block text-[13px] text-text-muted'}>
          {label}
        </span>
        <span className={compact ? 'text-[26px] font-semibold leading-none text-text' : 'order-1 block text-[44px] font-semibold leading-none text-text'}>
          {value}
        </span>
      </div>

      <div className="relative">
        <span className={TRACK_CLASS} />
        <span
          className="pointer-events-none absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-accent"
          style={{ left: 0, width: `${percent(value)}%` }}
        />
        <input
          type="range"
          min={MIN_AGE}
          max={MAX_AGE}
          step={1}
          value={value}
          aria-label={label}
          onChange={(event) => handleChange(Number(event.target.value))}
          className={`relative ${THUMB_CLASS}`}
        />
      </div>

      <div className="flex justify-between text-[12px] text-text-muted">
        <span>{MIN_AGE}</span>
        <span>{MAX_AGE}</span>
      </div>
    </div>
  );
}

export function AgeRangeSlider({
  min,
  max,
  onChange,
  labelFrom,
  labelTo,
  label,
  compact = false,
}: {
  min: number;
  max: number;
  onChange: (range: { min: number; max: number }) => void;
  labelFrom: string;
  labelTo: string;
  label?: string;
  compact?: boolean;
}): ReactNode {
  // Границы не должны «перепрыгивать» друг друга: тянущийся ползунок толкает
  // второй, а не меняется с ним местами — иначе жест теряет ползунок.
  const setMin = (next: number) => {
    haptic.select();
    onChange({ min: Math.min(next, max), max });
  };
  const setMax = (next: number) => {
    haptic.select();
    onChange({ min, max: Math.max(next, min) });
  };

  return (
    <div className={compact ? 'rounded-2xl border border-border bg-surface px-4 py-3' : 'rounded-2xl border border-border bg-surface p-5'}>
      <div className={compact ? 'mb-1 flex items-baseline justify-between' : 'mb-4 flex items-baseline justify-center gap-2'}>
        {compact && label ? <span className="text-[13px] text-text-muted">{label}</span> : null}
        <span className="flex items-baseline gap-1.5">
          <span className={compact ? 'text-[26px] font-semibold leading-none text-text' : 'text-[36px] font-semibold leading-none text-text'}>{min}</span>
          <span className={compact ? 'text-[15px] text-text-muted' : 'text-[20px] text-text-muted'}>—</span>
          <span className={compact ? 'text-[26px] font-semibold leading-none text-text' : 'text-[36px] font-semibold leading-none text-text'}>{max}</span>
        </span>
      </div>

      <div className="relative h-11">
        <span className={TRACK_CLASS} />
        <span
          className="pointer-events-none absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-accent"
          style={{ left: `${percent(min)}%`, width: `${percent(max) - percent(min)}%` }}
        />
        {/* Оба ползунка лежат друг на друге; клики принимает только сам thumb. */}
        <input
          type="range"
          min={MIN_AGE}
          max={MAX_AGE}
          step={1}
          value={min}
          aria-label={labelFrom}
          onChange={(event) => setMin(Number(event.target.value))}
          className={`pointer-events-none absolute inset-0 ${THUMB_CLASS}`}
        />
        <input
          type="range"
          min={MIN_AGE}
          max={MAX_AGE}
          step={1}
          value={max}
          aria-label={labelTo}
          onChange={(event) => setMax(Number(event.target.value))}
          className={`pointer-events-none absolute inset-0 ${THUMB_CLASS}`}
        />
      </div>

      <div className="flex justify-between text-[12px] text-text-muted">
        <span>{MIN_AGE}</span>
        <span>{MAX_AGE}</span>
      </div>
    </div>
  );
}
